import { authClient, useSession } from "@/lib/auth-client";
import { ShieldCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { BackupCodesStep } from "./enrollment-flow";

export function EnrolledPanel() {
	const { data: session } = useSession();
	const user = session?.user;

	return (
		<div className="space-y-6">
			<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
				<div className="flex items-start gap-3">
					<ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#16a34a]" strokeWidth={1.5} />
					<div className="flex-1">
						<h2 className="text-sm font-semibold text-[#1e1e20]">Two-factor is on</h2>
						<p className="mt-1 text-sm text-[#6b7280]">
							Signing in as <strong>{user?.email}</strong> requires a code from your authenticator
							app, an emailed code, or a backup code.
						</p>
					</div>
				</div>
			</div>

			<RegenerateBackupCodes />
			<DisableTwoFactor />
		</div>
	);
}

function RegenerateBackupCodes() {
	const [open, setOpen] = useState(false);
	const [password, setPassword] = useState("");
	const [codes, setCodes] = useState<string[] | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			const result = await authClient.twoFactor.generateBackupCodes({ password });
			if (result.error) {
				setError(result.error.message ?? "Failed to regenerate codes");
				return;
			}
			if (result.data?.backupCodes) {
				setCodes(result.data.backupCodes);
				setPassword("");
			}
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Failed to regenerate codes");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
			<h2 className="text-sm font-semibold text-[#1e1e20]">Backup codes</h2>
			<p className="mt-1 text-sm text-[#6b7280]">
				Regenerate if you've used them or think they may be compromised. The old set is invalidated
				immediately.
			</p>

			{!open && !codes && (
				<button
					type="button"
					onClick={() => setOpen(true)}
					className="mt-4 h-9 rounded border border-[#d1d5db] bg-white px-4 text-sm font-medium text-[#1e1e20] hover:bg-[#f8f9fb]"
				>
					Regenerate backup codes
				</button>
			)}

			{open && !codes && (
				<form onSubmit={handleSubmit} className="mt-4 max-w-sm space-y-3">
					{error && <div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{error}</div>}
					<input
						type="password"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						required
						placeholder="Confirm password"
						autoComplete="current-password"
						className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					/>
					<div className="flex items-center gap-2">
						<button
							type="submit"
							disabled={loading || !password}
							className="h-9 rounded bg-[#16a34a] px-4 text-sm font-semibold text-white hover:bg-[#15803d] disabled:opacity-50"
						>
							{loading ? "Regenerating…" : "Regenerate"}
						</button>
						<button
							type="button"
							onClick={() => {
								setOpen(false);
								setPassword("");
								setError(null);
							}}
							className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm font-medium text-[#6b7280] hover:bg-[#f8f9fb]"
						>
							Cancel
						</button>
					</div>
				</form>
			)}

			{codes && (
				<div className="mt-4">
					<BackupCodesStep
						codes={codes}
						onDone={() => {
							setCodes(null);
							setOpen(false);
						}}
					/>
				</div>
			)}
		</div>
	);
}

function DisableTwoFactor() {
	const [open, setOpen] = useState(false);
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			const result = await authClient.twoFactor.disable({ password });
			if (result.error) {
				setError(result.error.message ?? "Failed to disable 2FA");
				return;
			}
			toast.success("2FA disabled. You'll need to re-enroll to re-enable it.");
			window.location.reload();
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Failed to disable 2FA");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="rounded-lg border border-[#fecaca] bg-white p-6">
			<h2 className="text-sm font-semibold text-[#dc2626]">Disable two-factor</h2>
			<p className="mt-1 text-sm text-[#6b7280]">
				Removes 2FA from your account. Not recommended — Paul's security policy requires 2FA for
				everyone.
			</p>

			{!open && (
				<button
					type="button"
					onClick={() => setOpen(true)}
					className="mt-4 h-9 rounded border border-[#fecaca] bg-white px-4 text-sm font-medium text-[#dc2626] hover:bg-[#fef2f2]"
				>
					Disable 2FA
				</button>
			)}

			{open && (
				<form onSubmit={handleSubmit} className="mt-4 max-w-sm space-y-3">
					{error && <div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{error}</div>}
					<input
						type="password"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						required
						placeholder="Confirm password"
						autoComplete="current-password"
						className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					/>
					<div className="flex items-center gap-2">
						<button
							type="submit"
							disabled={loading || !password}
							className="h-9 rounded bg-[#dc2626] px-4 text-sm font-semibold text-white hover:bg-[#b91c1c] disabled:opacity-50"
						>
							{loading ? "Disabling…" : "Yes, disable 2FA"}
						</button>
						<button
							type="button"
							onClick={() => {
								setOpen(false);
								setPassword("");
								setError(null);
							}}
							className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm font-medium text-[#6b7280] hover:bg-[#f8f9fb]"
						>
							Cancel
						</button>
					</div>
				</form>
			)}
		</div>
	);
}
